import { useEffect, useRef } from "react";

// ---------------------------------------------------------------------------
// Styles disponibles — exposés pour la galerie (/waveforms) et le choix CallStage.
// ---------------------------------------------------------------------------
export const WAVEFORM_VARIANTS = [
  { id: "bars", labelFr: "Barres miroir", labelEn: "Mirror bars", descFr: "Barres symétriques autour de l'axe — le style historique, version ouverte." },
  { id: "equalizer", labelFr: "Égaliseur", labelEn: "Equalizer", descFr: "Barres ancrées en bas, façon égaliseur de chaîne hi-fi." },
  { id: "wave", labelFr: "Onde pleine", labelEn: "Filled wave", descFr: "Ruban d'onde lissé et rempli, type assistant vocal." },
  { id: "pulse", labelFr: "Orbe", labelEn: "Orb", descFr: "Halo radial qui respire et enfle quand la voix porte." },
  { id: "dots", labelFr: "Points", labelEn: "Dots", descFr: "Chapelet de points qui rebondissent et grossissent." },
  { id: "line", labelFr: "Double trait", labelEn: "Twin line", descFr: "Deux fins traits miroir, épuré et discret." },
] as const;

export type WaveformVariant = (typeof WAVEFORM_VARIANTS)[number]["id"];

interface AudioWaveformProps {
  /**
   * Flux audio temps réel de l'agent (exposé par useRealtimeSession). Si
   * l'AnalyserNode reçoit un vrai signal, la waveform suit fidèlement la voix
   * (puissance + durée). Sinon (analyseur muet, vieux Chrome, ou démo galerie),
   * bascule sur une animation synthétisée pilotée par `active`.
   */
  stream?: MediaStream | null;
  /** True quand l'agent parle : amplification + virage de couleur vers l'accent vif. */
  active: boolean;
  /** Couleur d'accent (hex). Défaut : bleu nuit charte BizCoach. */
  color?: string;
  /** Style de rendu. Défaut : barres miroir. */
  variant?: WaveformVariant;
  /** Nombre d'échantillons (barres / points / nœuds d'onde). */
  bars?: number;
  className?: string;
}

// ---------------------------------------------------------------------------
// Helpers couleur — interpolation hex pour le virage idle → accent → vif.
// ---------------------------------------------------------------------------
function hexToRgb(hex: string): [number, number, number] {
  let h = hex.replace("#", "");
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  const n = parseInt(h.slice(0, 6), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}
function mix(a: string, b: string, t: number): string {
  const [ar, ag, ab] = hexToRgb(a);
  const [br, bg, bb] = hexToRgb(b);
  const k = Math.max(0, Math.min(1, t));
  const r = Math.round(ar + (br - ar) * k);
  const g = Math.round(ag + (bg - ag) * k);
  const bl = Math.round(ab + (bb - ab) * k);
  return `rgb(${r},${g},${bl})`;
}

/**
 * Visualiseur de la voix de l'agent vocal, en plusieurs styles (cf.
 * WAVEFORM_VARIANTS). Au repos, la forme reste « ouverte » (onde douce visible,
 * couleur atténuée) plutôt que plate ; quand la voix s'active, elle s'amplifie
 * et la couleur vire de l'idle grisé vers l'accent puis un ton vif.
 *
 * Mode réel (préféré) : spectre FFT du flux audio. Mode synthé (secours/démo) :
 * mouvement type parole piloté par `active`.
 */
export function AudioWaveform({
  stream,
  active,
  color = "#384872",
  variant = "bars",
  bars = 40,
  className,
}: AudioWaveformProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const rafRef = useRef<number | null>(null);
  const activeRef = useRef(active);
  const colorRef = useRef(color);
  const variantRef = useRef<WaveformVariant>(variant);
  const levelsRef = useRef<number[]>(new Array(bars).fill(0));
  const energyLevelRef = useRef(0); // énergie globale lissée (0..1) — orbe/onde
  // Bascule définitive en mode réel dès qu'on capte un vrai signal audio.
  const realModeRef = useRef(false);

  activeRef.current = active;
  colorRef.current = color;
  variantRef.current = variant;

  // Branchement de l'analyseur sur le flux.
  useEffect(() => {
    realModeRef.current = false;
    if (!stream) return;
    const AudioCtx =
      window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    if (!AudioCtx) return;

    const audioCtx = new AudioCtx();
    audioCtxRef.current = audioCtx;
    audioCtx.resume().catch(() => {});

    let source: MediaStreamAudioSourceNode | null = null;
    try {
      source = audioCtx.createMediaStreamSource(stream);
      const analyser = audioCtx.createAnalyser();
      analyser.fftSize = 256;
      analyser.smoothingTimeConstant = 0.6;
      source.connect(analyser);
      analyserRef.current = analyser;
    } catch {
      analyserRef.current = null;
    }

    return () => {
      analyserRef.current = null;
      try {
        source?.disconnect();
      } catch {
        /* noop */
      }
      audioCtx.close().catch(() => {});
      audioCtxRef.current = null;
    };
  }, [stream]);

  // Boucle de dessin — montée une seule fois.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const freq = new Uint8Array(128); // fftSize 256 → 128 bins

    const resize = () => {
      const dpr = window.devicePixelRatio || 1;
      const w = canvas.clientWidth || 300;
      const h = canvas.clientHeight || 64;
      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener("resize", resize);

    let tick = 0;
    const draw = () => {
      rafRef.current = requestAnimationFrame(draw);
      tick++;
      const w = canvas.clientWidth || 300;
      const h = canvas.clientHeight || 64;
      ctx.clearRect(0, 0, w, h);

      const isActive = activeRef.current;
      const analyser = analyserRef.current;
      const baseColor = colorRef.current;

      // Lecture du spectre réel + énergie globale (0..1).
      let energy = 0;
      if (analyser) {
        if (audioCtxRef.current?.state === "suspended") audioCtxRef.current.resume().catch(() => {});
        analyser.getByteFrequencyData(freq);
        let sum = 0;
        for (let k = 1; k < 64; k++) sum += freq[k] ?? 0;
        energy = sum / (63 * 255);
        if (energy > 0.012) realModeRef.current = true; // signal réel détecté
      }
      const useReal = realModeRef.current;

      const levels = levelsRef.current;
      const center = bars / 2;
      const GAIN = 1.7;

      // 1) Niveaux par échantillon (barres / points / nœuds d'onde).
      for (let i = 0; i < bars; i++) {
        const distN = Math.abs(i - center) / center; // 0 au centre, 1 aux bords
        const envelope = 1 - distN * 0.6;

        let target = 0;
        if (useReal) {
          const bin = 1 + Math.floor((1 - distN) * 46); // bins 1..47 (voix)
          const v = (freq[bin] ?? 0) / 255;
          target = Math.min(1, Math.max(v, energy * 0.45) * GAIN) * envelope;
        } else if (isActive) {
          // Fallback synthé (analyseur muet / démo) — mouvement type parole.
          const a = Math.sin(tick * 0.22 + i * 0.55);
          const b = Math.sin(tick * 0.09 + i * 0.3);
          const c = Math.sin(tick * 0.35 - i * 0.2);
          const synth = 0.3 + 0.7 * Math.abs(a * 0.5 + b * 0.3 + c * 0.2);
          target = Math.min(1, synth * GAIN) * envelope;
        }

        // Plancher de repos « ouvert » : onde douce et bien visible appliquée
        // DANS TOUS LES CAS (y compris en mode réel, où les silences
        // retombaient à plat). La voix, quand elle parle, passe par-dessus.
        const idleFloor = (0.2 + 0.12 * Math.abs(Math.sin(tick * 0.045 + i * 0.5))) * envelope;
        target = Math.max(target, idleFloor);

        const cur = levels[i] ?? 0;
        levels[i] = target > cur ? cur + (target - cur) * 0.55 : cur + (target - cur) * 0.22;
      }

      // 2) Énergie globale lissée (orbe / opacité de l'onde / virage couleur).
      let energyTarget: number;
      if (useReal) energyTarget = Math.min(1, energy * 1.6);
      else if (isActive) energyTarget = 0.45 + 0.4 * Math.abs(Math.sin(tick * 0.13));
      else energyTarget = 0.12 + 0.05 * Math.abs(Math.sin(tick / 18));
      const eCur = energyLevelRef.current;
      energyLevelRef.current =
        energyTarget > eCur ? eCur + (energyTarget - eCur) * 0.5 : eCur + (energyTarget - eCur) * 0.12;
      const energyLevel = energyLevelRef.current;
      const intensity = Math.min(1, energyLevel * 1.5);

      // Couleur évolutive : idle grisé → accent → ton vif.
      const idleColor = mix(baseColor, "#9aa6bf", 0.5);
      const hotColor = mix(baseColor, "#ffffff", 0.3);
      const shade = (level: number): string => {
        const l = Math.max(0, Math.min(1, level));
        return l < 0.5 ? mix(idleColor, baseColor, l * 2) : mix(baseColor, hotColor, (l - 0.5) * 2);
      };
      const accent = shade(intensity);

      // 3) Rendu selon le style.
      const v = variantRef.current;
      if (v === "bars" || v === "equalizer") {
        const gap = 2;
        const barW = Math.max(2, w / bars - gap);
        const radius = barW / 2;
        for (let i = 0; i < bars; i++) {
          const lvl = levels[i] ?? 0;
          const barH = Math.max(2, lvl * (h - 4));
          const x = i * (barW + gap);
          const y = v === "equalizer" ? h - barH : h / 2 - barH / 2;
          ctx.fillStyle = shade(lvl);
          ctx.globalAlpha = lvl > 0.12 ? 0.95 : 0.45;
          ctx.beginPath();
          if (typeof ctx.roundRect === "function") ctx.roundRect(x, y, barW, barH, radius);
          else ctx.rect(x, y, barW, barH);
          ctx.fill();
        }
        ctx.globalAlpha = 1;
      } else if (v === "wave") {
        const mid = h / 2;
        const amp = h * 0.46;
        const pt = (i: number) => [(i / (bars - 1)) * w, (levels[i] ?? 0) * amp] as const;
        // Contour supérieur puis inférieur (miroir) → forme fermée remplie.
        ctx.beginPath();
        ctx.moveTo(0, mid - (levels[0] ?? 0) * amp);
        for (let i = 1; i < bars; i++) {
          const [x, a] = pt(i);
          const [px] = pt(i - 1);
          ctx.quadraticCurveTo(px + (x - px) / 2, mid - a, x, mid - a);
        }
        for (let i = bars - 1; i >= 0; i--) {
          const [x, a] = pt(i);
          ctx.lineTo(x, mid + a);
        }
        ctx.closePath();
        ctx.globalAlpha = 0.25 + intensity * 0.45;
        ctx.fillStyle = accent;
        ctx.fill();
        // Liseré supérieur net.
        ctx.globalAlpha = 1;
        ctx.beginPath();
        ctx.moveTo(0, mid - (levels[0] ?? 0) * amp);
        for (let i = 1; i < bars; i++) {
          const [x, a] = pt(i);
          const [px] = pt(i - 1);
          ctx.quadraticCurveTo(px + (x - px) / 2, mid - a, x, mid - a);
        }
        ctx.lineWidth = 2.5;
        ctx.strokeStyle = accent;
        ctx.stroke();
      } else if (v === "pulse") {
        const cx = w / 2;
        const cy = h / 2;
        const baseR = Math.min(w, h) * 0.13;
        const reach = Math.min(w, h) * 0.34;
        // Anneaux concentriques décroissants.
        for (let r = 0; r < 3; r++) {
          const rr = baseR + reach * energyLevel * (1 + r * 0.5);
          ctx.beginPath();
          ctx.arc(cx, cy, rr, 0, Math.PI * 2);
          ctx.globalAlpha = (0.32 - r * 0.09) * (0.5 + intensity * 0.5);
          ctx.fillStyle = accent;
          ctx.fill();
        }
        // Cœur plein.
        ctx.globalAlpha = 1;
        ctx.beginPath();
        ctx.arc(cx, cy, baseR * (0.7 + 0.4 * energyLevel), 0, Math.PI * 2);
        ctx.fillStyle = accent;
        ctx.fill();
      } else if (v === "dots") {
        const n = Math.min(bars, 28);
        const step = w / n;
        const r0 = Math.min(step * 0.22, h * 0.07);
        const mid = h / 2;
        for (let j = 0; j < n; j++) {
          const lvl = levels[Math.floor((j / n) * bars)] ?? 0;
          const x = step * (j + 0.5);
          const y = mid - (lvl - 0.4) * h * 0.5;
          const r = r0 + lvl * Math.min(step * 0.3, h * 0.12);
          ctx.beginPath();
          ctx.arc(x, y, r, 0, Math.PI * 2);
          ctx.globalAlpha = 0.5 + lvl * 0.5;
          ctx.fillStyle = shade(lvl);
          ctx.fill();
        }
        ctx.globalAlpha = 1;
      } else if (v === "line") {
        const mid = h / 2;
        const amp = h * 0.42;
        const trace = (sign: number) => {
          ctx.beginPath();
          ctx.moveTo(0, mid - sign * (levels[0] ?? 0) * amp);
          for (let i = 1; i < bars; i++) {
            const x = (i / (bars - 1)) * w;
            const px = ((i - 1) / (bars - 1)) * w;
            const y = mid - sign * (levels[i] ?? 0) * amp;
            ctx.quadraticCurveTo(px + (x - px) / 2, y, x, y);
          }
          ctx.lineWidth = 2;
          ctx.strokeStyle = accent;
          ctx.globalAlpha = sign > 0 ? 1 : 0.45;
          ctx.stroke();
        };
        trace(1);
        trace(-1);
        ctx.globalAlpha = 1;
      }
    };
    draw();

    return () => {
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
      window.removeEventListener("resize", resize);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bars]);

  return (
    <canvas
      ref={canvasRef}
      className={className}
      style={{ width: "100%", height: "100%", display: "block" }}
      aria-hidden="true"
    />
  );
}
